/*
  industrialkonzept Next, part 4 (2026-09-23)
  Colour and shape dots on product tiles, everywhere tiles appear (collections, search,
  homepage rails, "more from" and related products on product pages):
  - one tile per product stays; below it the colours (or shapes) as small picture dots and "7 colours · 4 sizes"
  - hovering or tapping a dot shows that colour and links the tile to it
  - in collections with a colour filter active, the tile shows the matching colour right away
  - products on hand say so honestly: "Ready to ship" when every version is on hand, otherwise
    "Dark blue ready to ship", "Chrome: 1 of 8 versions ready to ship" or "3 of 8 colours ready
    to ship" (hollow dot); hovering a colour that is not on hand hides the line
  Data: collection tiles from /collections/<handle>?view=ikn-swatches (same filters and sorting),
  all other tiles per product from /products/<handle>?view=ikn-swatches.
  Also (see the end): long product galleries start folded on desktop, "Ready to ship" in the cart,
  quick add on the homepage cover, the designer next to the brand on product pages.
  Optional: without this script the shop works as before.
*/
(() => {
  if (window.__ikShop) return;
  window.__ikShop = true;

  const root = document.documentElement;
  const inCollection = root.getAttribute('data-ikn-template') === 'collection';
  const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const T = (k, v) => (window.ikT ? window.ikT(k, v) : k);
  const abs = (u) => (u && u.startsWith('//') ? location.protocol + u : u || '');
  const widthOf = (u, w) => {
    const clean = abs(u).replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    return clean + (clean.includes('?') ? '&' : '?') + 'width=' + w;
  };

  // Which colours are filtered right now (labels of the ticked colour filters)
  const activeColours = () =>
    [...document.querySelectorAll('input[type="checkbox"][name^="filter."]:checked')]
      .filter((i) => /colou?r|farbe|color-pattern/i.test(i.name))
      .map((i) => {
        const label = document.querySelector('label[for="' + i.id + '"]') || i.closest('label');
        return ((label && label.textContent) || i.value || '').replace(/\(\d+\)/, '').trim().toLowerCase();
      })
      .filter(Boolean);

  const data = {};
  const stock = new Map(); // handle -> [versions on hand, all versions]
  const seen = new Set();
  const fetched = new Set();
  let pages = 1;
  const fetchPage = (page) => {
    if (fetched.has(page)) return Promise.resolve();
    fetched.add(page);
    const url = new URL(location.href);
    url.searchParams.set('view', 'ikn-swatches');
    url.searchParams.set('page', page);
    // no JSON Accept header: Shopify would answer with its own collection JSON instead
    return fetch(url.toString())
      .then((r) => (r.ok ? r.text() : ''))
      .then((t) => {
        try {
          const json = JSON.parse(t);
          pages = json.pages || pages;
          Object.assign(data, json.products || {});
          Object.entries(json.stock || {}).forEach(([h, st]) => stock.set(h, st));
          (json.all || []).forEach((h) => seen.add(h));
        } catch (e) {
          /* no swatches, tiles stay as they are */
        }
      })
      .catch(() => {});
  };

  // Tiles outside the collection grid: one small request per product, a few at a time,
  // remembered for this visit
  const single = new Map();
  const queue = [];
  let running = 0;
  const pump = () => {
    while (running < 4 && queue.length) {
      const h = queue.shift();
      running++;
      fetch(base + 'products/' + encodeURIComponent(h) + '?view=ikn-swatches')
        .then((r) => (r.ok ? r.text() : 'null'))
        .then((t) => {
          let rec = null;
          try {
            rec = JSON.parse(t);
          } catch (e) {
            /* nothing to add for this one */
          }
          single.set(h, rec);
          try {
            sessionStorage.setItem('ikn-t3:' + h, JSON.stringify(rec));
          } catch (e) {}
        })
        .catch(() => single.set(h, null))
        .finally(() => {
          running--;
          decorate();
          pump();
        });
    }
  };
  // Answers {st: [on hand, all], d: dot data}, null when there is nothing, undefined while asking
  const lookup = (h) => {
    if (single.has(h)) return single.get(h);
    try {
      const cached = sessionStorage.getItem('ikn-t3:' + h);
      if (cached !== null) {
        single.set(h, JSON.parse(cached));
        return single.get(h);
      }
    } catch (e) {}
    single.set(h, undefined);
    queue.push(h);
    pump();
    return undefined;
  };

  const handleOf = (href) => {
    const m = (href || '').match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  };

  // The two kinds of tiles: the theme's (collections, search, related products) and our rail cards
  const TILES = 'li > data-island.relative.flex.h-full.flex-col, a.ikh-card';
  const isCard = (tile) => tile.matches('a.ikh-card');
  const imageOf = (tile) => tile.querySelector(isCard(tile) ? '.ikh-card__media img' : '.group img');
  const infoOf = (tile) => tile.querySelector(isCard(tile) ? '.ikh-card__meta' : ':scope > .relative > div.py-4');
  const linksOf = (tile) => (isCard(tile) ? [tile] : [...tile.querySelectorAll('a[href*="/products/"]')]);

  const showColour = (tile, sw) => {
    const [, , big, vid] = sw;
    const img = imageOf(tile);
    tile.classList.add('is-ikn-sw');
    if (big && img) {
      img.srcset = [360, 540, 720, 960].map((w) => widthOf(big, w) + ' ' + w + 'w').join(', ');
      img.src = widthOf(big, 720);
    }
    if (vid) {
      linksOf(tile).forEach((a) => {
        const u = new URL(a.getAttribute('href'), location.origin);
        u.searchParams.set('variant', vid);
        a.setAttribute('href', u.pathname + u.search);
      });
    }
    tile.querySelectorAll('[data-ikn-sw]').forEach((b) => b.setAttribute('aria-pressed', String(b.__sw === sw)));
    const badge = tile.querySelector('.ikn-stock');
    if (badge) {
      // The chosen colour decides: on hand says so by name (and in how many sizes), otherwise the line steps aside
      badge.classList.toggle('is-off', !(sw[4] > 0));
      if (sw[4] > 0 && badge.__d) {
        badge.textContent = lookText(sw, badge.__d);
        badge.classList.toggle('is-part', sw[4] < sw[5] || badge.__partly);
      }
    }
  };

  // One colour on hand: "Chrome ready to ship", or "Chrome: 1 of 8 versions ready to ship"
  // when only some of its sizes are there. Two options (colour + size) say sizes, more say versions.
  const lookText = (sw, d) => {
    const [name, , , , n, all] = sw;
    if (!(n < all)) return T('shop.look_ready', { name });
    const sizes = d.o === 2 && d.z > 1;
    return T(sizes ? 'shop.look_part_sizes' : 'shop.look_part_versions', { name, n, all });
  };

  // "Ready to ship", honest about how much of it is on hand
  const stockBadge = (st, d) => {
    const [n, all] = st;
    const badge = document.createElement('span');
    badge.className = 'ikn-stock';
    if (n >= all) {
      badge.textContent = T('shop.ready');
      return badge;
    }
    badge.classList.add('is-part');
    badge.__partly = true;
    badge.__d = d;
    const kinds = { c: 'shop.onhand_colours', f: 'shop.onhand_shapes', v: 'shop.versions_ready' };
    const onHand = d ? d.s.filter((sw) => sw[4] > 0) : [];
    if (onHand.length === 1) {
      badge.textContent = lookText(onHand[0], d);
    } else if (onHand.length > 1) {
      badge.textContent = T(kinds[d.k] || kinds.c, { n: onHand.length, all: d.c });
    } else {
      badge.textContent = T('shop.versions_ready', { n, all });
    }
    return badge;
  };

  // A tile gets its dots once it comes near the screen: rails far down the homepage or scrolled
  // sideways ask for their data (and dot pictures) only when someone gets there
  const near = new WeakSet();
  const nearIO =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            let any = false;
            entries.forEach((en) => {
              if (!en.isIntersecting) return;
              near.add(en.target);
              nearIO.unobserve(en.target);
              any = true;
            });
            if (any) decorate();
          },
          { rootMargin: '600px 400px' }
        )
      : null;

  const decorate = () => {
    const wanted = inCollection ? activeColours() : [];
    document.querySelectorAll(TILES).forEach((tile) => {
      if (tile.__iknSw) return;
      if (nearIO && !near.has(tile)) {
        if (!tile.__iknWatch) {
          tile.__iknWatch = true;
          nearIO.observe(tile);
        }
        return;
      }
      const link = linksOf(tile)[0];
      const info = infoOf(tile);
      const h = link && handleOf(link.getAttribute('href'));
      if (!h || !info) return;
      // Collection tiles wait for the collection data; everything else asks per product
      let d;
      let st;
      if (inCollection && tile.closest('ul.collection-list')) {
        if (!data[h] && !seen.has(h)) return; // its data page is still loading
        d = data[h] || null;
        st = stock.get(h);
      } else {
        const rec = lookup(h);
        if (rec === undefined) return; // asked, decorate() runs again when it arrives
        d = (rec && rec.d) || null;
        st = rec && rec.st && rec.st[0] > 0 ? rec.st : null;
      }
      tile.__iknSw = true;
      if (!d) {
        if (st) info.appendChild(stockBadge(st, null));
        return;
      }
      const max = 6;
      const row = document.createElement('div');
      row.className = 'ikn-sw';
      // Only colours with a picture of their own get a dot (they still count in the label)
      const pics = d.s.filter((sw) => sw[1]);
      pics.slice(0, max).forEach((sw) => {
        const [name, small] = sw;
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ikn-sw__dot';
        b.setAttribute('data-ikn-sw', '');
        b.setAttribute('aria-label', name);
        b.title = name;
        b.__sw = sw;
        b.style.backgroundImage = 'url("' + widthOf(small, 120) + '")';
        row.appendChild(b);
      });
      if (pics.length > max) {
        const more = document.createElement('span');
        more.className = 'ikn-sw__more';
        more.textContent = '+' + (pics.length - max);
        row.appendChild(more);
      }
      const label = document.createElement('span');
      label.className = 'ikn-sw__label';
      const kinds = { c: 'shop.count_colours', f: 'shop.count_shapes', v: 'shop.count_versions' };
      label.textContent = T(kinds[d.k] || kinds.c, { n: d.c }) + (d.z > 1 && (d.k !== 'f' || d.z > d.c) ? ' · ' + T('shop.count_sizes', { n: d.z }) : '');
      row.appendChild(label);
      if (st) info.appendChild(stockBadge(st, d));
      info.appendChild(row);

      // A colour filter is active: show that colour straight away
      if (wanted.length) {
        const match = d.s.find((sw) => sw[2] && wanted.some((w) => sw[0].toLowerCase().includes(w) || w.includes(sw[0].toLowerCase())));
        if (match) showColour(tile, match);
      }
    });
  };

  // Hover on devices with a mouse, tap everywhere
  document.addEventListener('mouseover', (e) => {
    const b = e.target.closest('[data-ikn-sw]');
    if (b && b.__sw) showColour(b.closest(TILES), b.__sw);
  });
  document.addEventListener(
    'click',
    (e) => {
      const b = e.target.closest('[data-ikn-sw]');
      if (!b) return;
      e.preventDefault();
      e.stopPropagation();
      if (b.__sw) showColour(b.closest(TILES), b.__sw);
    },
    true
  );

  const run = async () => {
    decorate();
    if (!inCollection || !document.querySelector('ul.collection-list')) return; // e.g. the designers A–Z page
    // The grid shows fewer products per page (24) than a data page holds (50): find the data page that
    // holds this grid page's first product
    const gridPage = +(new URL(location.href).searchParams.get('page') || 1);
    let offset = 0;
    if (gridPage > 1) {
      const onPage = new Set([...document.querySelectorAll('ul.collection-list a[href*="/products/"]')].map((a) => handleOf(a.getAttribute('href')))).size;
      const total = +((document.querySelector('[data-ikn-count]') || {}).textContent || 0);
      // the last page can be shorter: then count back from the total
      offset = document.querySelector('link[rel="next"]') || !total ? (gridPage - 1) * onPage : Math.max(0, total - onPage);
    }
    const start = Math.floor(offset / 50) + 1;
    await fetchPage(start);
    decorate();
    // Tiles from infinite scroll or other pages: load the next data pages as needed
    for (let p = start + 1; p <= Math.min(pages, start + 8); p++) {
      const missing = [...document.querySelectorAll('ul.collection-list a[href*="/products/"]')].some((a) => {
        const tile = a.closest('data-island');
        return tile && !tile.__iknSw && !seen.has(handleOf(a.getAttribute('href')));
      });
      if (!missing) break;
      await fetchPage(p);
      decorate();
    }
  };
  run();

  // Filters, sorting and infinite scroll replace or add tiles
  let timer = 0;
  let lastSearch = location.search;
  new MutationObserver(() => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      if (inCollection && location.search !== lastSearch) {
        lastSearch = location.search;
        fetched.clear();
        seen.clear();
        stock.clear();
        Object.keys(data).forEach((k) => delete data[k]);
      }
      run();
    }, 300);
  }).observe(document.body, { childList: true, subtree: true });

  /* =====================================================================
     Product gallery: long galleries start folded (desktop only)
     Some products have 50 to 100 pictures, so the description was far away. The first six
     pictures stay (large, pair, large, pair); a button below shows the rest and folds it again.
     Folded pictures are only made tiny and invisible (ik-shop.css), not removed: the theme moves
     the chosen colour's picture to the top only when it can "see" it, and it can see tiny ones.
     After the theme moved a picture, the first six are counted again.
     Phones keep the theme's swipe gallery; the CSS only folds from 1024px.
     ===================================================================== */
  const FOLD_AT = 6;
  const gallery = document.querySelector('.product-media-container .splide__list');
  if (gallery && gallery.children.length > FOLD_AT + 2) {
    const total = gallery.children.length;
    const container = gallery.closest('.product-media-container');
    container.classList.add('ikn-fold', 'is-folded');

    const bar = document.createElement('div');
    bar.className = 'ikn-fold__bar';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ikn-fold__btn';
    const label = (open) =>
      open
        ? T('shop.fold_gallery')
        : T('shop.see_all_pictures', { n: total });
    btn.innerHTML =
      '<span class="ikn-fold__peek" aria-hidden="true"></span>' +
      '<span class="ikn-fold__text"></span>' +
      '<span class="ikn-fold__count" aria-hidden="true">+' + (total - FOLD_AT) + '</span>';
    const peek = btn.querySelector('.ikn-fold__peek');

    // first six in the current order stay, the rest folds; the button peeks at the next four
    const refold = () => {
      const items = [...gallery.children];
      items.forEach((li, i) => li.classList.toggle('ikn-fold__rest', i >= FOLD_AT));
      const next = items
        .slice(FOLD_AT, FOLD_AT + 4)
        .map((li) => li.querySelector('img'))
        .filter(Boolean)
        .map((img) => widthOf(img.currentSrc || img.src, 160));
      if (peek.dataset.src === next.join(' ')) return;
      peek.dataset.src = next.join(' ');
      peek.textContent = '';
      next.forEach((src) => {
        const small = document.createElement('img');
        small.src = src;
        small.alt = '';
        small.loading = 'lazy';
        peek.appendChild(small);
      });
    };
    refold();
    new MutationObserver(refold).observe(gallery, { childList: true });

    const setOpen = (open) => {
      container.classList.toggle('is-folded', !open);
      btn.setAttribute('aria-expanded', String(open));
      btn.querySelector('.ikn-fold__text').textContent = label(open);
      if (open) gallery.querySelectorAll('.ikn-fold__rest img[loading="lazy"]').forEach((img) => img.removeAttribute('loading'));
    };
    btn.addEventListener('click', () => {
      const open = container.classList.contains('is-folded');
      setOpen(open);
      // Folding from far down: land at the button instead of somewhere in the description
      if (!open) bar.scrollIntoView({ block: 'center' });
    });
    bar.appendChild(btn);
    gallery.after(bar);
    setOpen(false);
  }

  /* =====================================================================
     Phone gallery: a progress line and "03 / 34" under the picture instead of the theme's dots and
     arrows (hidden in ik-next-ui.css); the line doubles as a scrubber for fast browsing. It moves with the
     pictures while they move and settles on the theme's own active slide, so swiping, variant changes and
     the zoom all keep it right. Computers show the pictures as a grid and skip it.
     ===================================================================== */
  const stageList = document.querySelector('.product-media-container .splide .splide__list');
  if (stageList) {
    const track = stageList.closest('.splide__track');
    const count = document.createElement('div');
    count.className = 'ikn-gcount';
    count.innerHTML = '<span class="ikn-gcount__bar"><span></span></span><span class="ikn-gcount__n"></span>';
    const fill = count.querySelector('.ikn-gcount__bar > span');
    const text = count.querySelector('.ikn-gcount__n');
    const pad = (n) => (n < 10 ? '0' : '') + n;
    let last = '';
    const update = () => {
      const slides = [...stageList.children].filter((li) => li.classList.contains('splide__slide') && !li.classList.contains('splide__slide--clone'));
      const i = Math.max(0, slides.findIndex((li) => li.classList.contains('is-active')));
      const now = i + '/' + slides.length;
      if (now === last) return;
      last = now;
      count.hidden = slides.length < 2;
      text.textContent = pad(i + 1) + ' / ' + pad(slides.length);
      fill.style.width = ((i + 1) / slides.length) * 100 + '%';
    };
    track.after(count);

    // The line follows the finger: while the pictures move (swipe, scrubbing, a variant change) it is
    // painted every frame from where the pictures actually are, not only once the slider has settled
    const live = () => {
      const slides = [...stageList.children].filter((li) => li.classList.contains('splide__slide') && !li.classList.contains('splide__slide--clone'));
      if (slides.length < 2) return;
      const first = slides[0].getBoundingClientRect().left;
      const step = slides[1].getBoundingClientRect().left - first || 1;
      const pos = Math.min(slides.length - 1, Math.max(0, (track.getBoundingClientRect().left - first) / step));
      fill.style.width = ((pos + 1) / slides.length) * 100 + '%';
      text.textContent = pad(Math.round(pos) + 1) + ' / ' + pad(slides.length);
    };
    let frame = 0;
    let still = 0;
    let painted = '';
    const loop = () => {
      live();
      still = fill.style.width === painted ? still + 1 : 0;
      painted = fill.style.width;
      // stops a moment after the pictures have come to rest
      frame = still < 12 ? requestAnimationFrame(loop) : 0;
    };
    const kick = () => {
      still = 0;
      if (!frame) frame = requestAnimationFrame(loop);
    };
    ['touchstart', 'touchmove', 'pointerdown'].forEach((type) => track.addEventListener(type, kick, { passive: true }));
    new MutationObserver(() => {
      update();
      kick();
    }).observe(stageList, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    update();

    // The line is a scrubber: drag a finger along it to fly through all pictures, tap to jump.
    // Uses the theme's own slider (its Alpine data), which changes pictures without delay.
    const bar = count.querySelector('.ikn-gcount__bar');
    const slider = () => {
      const host = stageList.closest('[x-data]');
      const data = host && host._x_dataStack && host._x_dataStack[0];
      return data && data.splide && typeof data.splide.go === 'function' ? data.splide : null;
    };
    bar.setAttribute('role', 'slider');
    bar.setAttribute('tabindex', '0');
    bar.setAttribute('aria-label', T('shop.gallery_scrub'));
    const sync = () => {
      const [i, n] = last.split('/').map(Number);
      bar.setAttribute('aria-valuemin', '1');
      bar.setAttribute('aria-valuemax', String(n));
      bar.setAttribute('aria-valuenow', String(i + 1));
      bar.setAttribute('aria-valuetext', text.textContent);
    };
    new MutationObserver(sync).observe(text, { childList: true, characterData: true, subtree: true });
    sync();
    let scrubbing = false;
    const goTo = (x) => {
      const s = slider();
      if (!s) return;
      const r = bar.getBoundingClientRect();
      const n = s.length;
      const i = Math.min(n - 1, Math.max(0, Math.floor(((x - r.left) / r.width) * n)));
      if (i !== s.index) s.go(i);
    };
    count.addEventListener('pointerdown', (e) => {
      if (!slider()) return;
      scrubbing = true;
      count.classList.add('is-scrubbing');
      try {
        count.setPointerCapture(e.pointerId);
      } catch (err) {}
      goTo(e.clientX);
    });
    count.addEventListener('pointermove', (e) => {
      if (scrubbing) goTo(e.clientX);
    });
    const stop = () => {
      scrubbing = false;
      count.classList.remove('is-scrubbing');
    };
    count.addEventListener('pointerup', stop);
    count.addEventListener('pointercancel', stop);
    bar.addEventListener('keydown', (e) => {
      const s = slider();
      if (!s) return;
      if (e.key === 'ArrowRight') s.go('>');
      else if (e.key === 'ArrowLeft') s.go('<');
      else return;
      e.preventDefault();
    });
  }

  /* =====================================================================
     Ready to ship in the cart (drawer and cart page), the same promise as on the tiles:
     "Ready to ship" when stock covers the quantity, "2 of 3 ready to ship" when it covers part.
     Data from /cart?view=ikn-stock; checked again whenever lines or quantities change.
     ===================================================================== */
  let cartBusy = false;
  let cartRetries = 0;
  const cartQty = (li) => {
    const input = li.querySelector('input[name="updates[]"], input[type="number"]');
    return input ? String(input.value) : '';
  };
  const cartStock = async (again) => {
    // Look again when the quantity changed, or when the theme redrew the line and took the label away;
    // lines the cart had not saved yet only on the timed retry (again), never from the page watcher
    const lines = [...document.querySelectorAll('li[data-line-item-key], tr[data-line-item-key]')].filter(
      (li) =>
        li.__iknQty !== cartQty(li) ||
        (li.__iknHas && !li.querySelector('.ikn-stock')) ||
        (again === true && li.__iknBehind)
    );
    if (!lines.length || cartBusy) return;
    cartBusy = true;
    try {
      const r = await fetch(base + 'cart?view=ikn-stock', { cache: 'no-store' });
      const json = JSON.parse(await r.text());
      let behind = false;
      lines.forEach((li) => {
        li.__iknQty = cartQty(li);
        const st = json.lines && json.lines[li.getAttribute('data-line-item-key')];
        // The theme shows a new quantity before the cart has saved it: ask again shortly
        li.__iknBehind = !!(st && li.__iknQty !== '' && Number(li.__iknQty) !== st[1]);
        if (li.__iknBehind) behind = true;
        let badge = li.querySelector('.ikn-stock');
        li.__iknHas = !!(st && st[0] > 0);
        if (!li.__iknHas) {
          if (badge) badge.remove();
          return;
        }
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'ikn-stock ikn-stock--cart';
          // under the variant name, before the error line
          const anchor = li.querySelector('.cart-item-error');
          if (anchor) anchor.before(badge);
          else (li.querySelector('a[href*="/products/"]:not(.block)') || li).after(badge);
        }
        const [n, qty] = st;
        badge.classList.toggle('is-part', n < qty);
        // only write when the words change, so the page watcher is not woken for nothing
        const txt = n >= qty ? T('shop.ready') : T('shop.cart_part', { n, all: qty });
        if (badge.textContent !== txt) badge.textContent = txt;
      });
      if (behind && cartRetries < 6) {
        cartRetries++;
        window.setTimeout(() => cartStock(true), 1200);
      } else if (!behind) {
        cartRetries = 0;
      }
    } catch (e) {
      /* no stock line, the cart works as before */
    } finally {
      cartBusy = false;
    }
  };
  let cartTimer = 0;
  new MutationObserver(() => {
    window.clearTimeout(cartTimer);
    cartTimer = window.setTimeout(cartStock, 250);
  }).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('change', (e) => {
    if (e.target.closest && e.target.closest('[data-line-item-key]')) window.setTimeout(cartStock, 900);
  });
  document.addEventListener('theme:update:cart', () => window.setTimeout(cartStock, 900));
  cartStock();

  /* =====================================================================
     Designer on product pages: "Designer: …" in the details, clickable. The link opens all
     products with the designer filter (Search & Discovery, custom.designer). The line
     "Niels Jørgen Haugesen for HAY" above the title is written by snippets/product-block-vendor.liquid.
     ===================================================================== */
  const productData = (() => {
    try {
      return JSON.parse((document.querySelector('script[data-ikn-product]') || {}).textContent || 'null');
    } catch (e) {
      return null;
    }
  })();
  if (productData && productData.designer) {
    const name = productData.designer;
    const link = () => {
      const a = document.createElement('a');
      a.href = productData.designerUrl;
      a.textContent = name;
      return a;
    };
    // The details and the "Properties" tab print the field on its own line.
    // Not when the brand is its own designer ("Frama"): then that line is the brand, not a designer,
    // and never the brand line above the title.
    if (!productData.designerIsBrand) {
      document.querySelectorAll('.product-content-container p').forEach((p) => {
        if (p.__iknDesigner || p.closest('.product-vendor-block') || p.textContent.trim() !== name) return;
        p.__iknDesigner = true;
        p.textContent = T('shop.designer') + ' ';
        p.appendChild(link());
      });
    }
  }

  // Featured designers as chips: in the brands dropdown (in every page's header) and on the brands page
  // of the phone menu (sections/menu-drawer.liquid calls window.ikFillDesigners when it opens).
  // One list per visit, from /collections/all?view=ikn-designer-list.
  let designersP = null;
  const designers = (wait) => {
    if (designersP) return designersP;
    let cached = null;
    try {
      cached = JSON.parse(sessionStorage.getItem('ikn-designers') || 'null');
    } catch (e) {}
    designersP = Array.isArray(cached)
      ? Promise.resolve(cached)
      : new Promise((done) => window.setTimeout(done, wait))
          .then(() => fetch(base + 'collections/all?view=ikn-designer-list'))
          .then((r) => (r.ok ? r.text() : '[]'))
          .then((t) => {
            const list = JSON.parse(t);
            // remembered for the visit, an empty answer too (asked again in the next visit)
            try {
              sessionStorage.setItem('ikn-designers', JSON.stringify(list));
            } catch (e) {}
            return list;
          })
          .catch(() => []);
    return designersP;
  };
  window.ikFillDesigners = (row, wait = 0) => {
    if (!row || row.__iknFilled) return;
    row.__iknFilled = true;
    designers(wait).then((list) => {
      if (!Array.isArray(list) || !list.length) return;
      const chips = row.querySelector('[data-ikn-designer-chips]');
      list.forEach(([name, count]) => {
        const a = document.createElement('a');
        a.className = 'ikn-chip';
        a.href = base + 'collections/all?filter.p.m.custom.designer=' + encodeURIComponent(name);
        a.textContent = name;
        const n = document.createElement('span');
        n.className = 'ikn-chip__n';
        n.textContent = count;
        a.appendChild(n);
        chips.appendChild(a);
      });
      row.hidden = false;
    });
  };
  // The brands dropdown asks for the list once the pointer or the keyboard reaches the main menu, so
  // visitors who never open it do not load it (the phone menu fills its own row when it opens)
  const megaDesigners = document.querySelector('[data-ikn-designers]');
  if (megaDesigners) {
    const nav = megaDesigners.closest('nav') || document;
    const fill = () => window.ikFillDesigners(megaDesigners);
    nav.addEventListener('pointerenter', fill, { once: true });
    nav.addEventListener('focusin', fill, { once: true });
  }

  /* =====================================================================
     Quick add (products on the homepage cover): one click, straight into the cart drawer.
     If anything goes wrong, the product page opens instead.
     ===================================================================== */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-ikn-quick-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.classList.contains('is-busy')) return;
    btn.classList.add('is-busy');
    try {
      // Same route as the theme's own buy button: it redraws the drawer lines, footer and count
      const r = await fetch(base + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: Number(btn.getAttribute('data-ikn-quick-add')),
          quantity: 1,
          sections: 'cart-items,cart-footer,cart-item-count,cart-live-region',
        }),
      });
      if (!r.ok) throw new Error('add failed');
      window.dispatchEvent(new CustomEvent('baseline:cart:afteradditem', { detail: { response: await r.json() } }));
      try {
        const modals = window.Alpine && window.Alpine.store && window.Alpine.store('modals');
        if (modals) modals.open('cart');
      } catch (x) {
        /* added anyway: the cart count shows it */
      }
      btn.classList.add('is-done');
      // A thin line tick, drawn like the arrows around it
      btn.firstElementChild.innerHTML =
        '<svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 8.5l3 3 6-7"/></svg>';
    } catch (err) {
      const link = btn.closest('li') && btn.closest('li').querySelector('a[href]');
      if (link) location.href = link.href;
    } finally {
      btn.classList.remove('is-busy');
    }
  });
  /* =====================================================================
     Filters as a bar above the products (collections and search, from 768px): the theme's own
     filter form becomes a row of buttons, each opening its panel; only one is open at a time and a
     click elsewhere or Esc closes it. Chosen filters appear as chips with ×, a click takes one off
     through the theme's own filter (the same checkbox). The theme redraws the form after each
     filter change, so this runs again on every change (styles in ik-shop.css).
     ===================================================================== */
  let barOpen = ''; // the group the visitor has open, kept open when the theme redraws the form
  const titleOf = (g) => ((g.querySelector(':scope > .ikn-fhead') || {}).textContent || '').replace(/\s+/g, ' ').trim().split(' ')[0];
  const filterBar = () => {
    const form = document.getElementById('filters-form-sidebar');
    if (!form || !form.parentElement) return;
    form.parentElement.classList.add('ikn-fbar-layout');
    form.classList.add('ikn-fbar');
    const groups = [...form.querySelectorAll('data-island[x-data]')].filter((g) => /expanded/.test(g.getAttribute('x-data') || ''));
    groups.forEach((g) => {
      // wrappers between the form and the groups step aside, so all buttons sit in one row
      for (let w = g.parentElement; w && w !== form; w = w.parentElement) w.classList.add('ikn-fbar__wrap');
      const btn = g.querySelector(':scope > .ikn-fhead > button');
      if (!btn) return;
      g.classList.add('ikn-fbar__group');
      if (!btn.__iknBar) {
        btn.__iknBar = true;
        btn.addEventListener('click', () => {
          const open = !g.classList.contains('is-open');
          form.querySelectorAll('.ikn-fbar__group.is-open').forEach((x) => x.classList.remove('is-open'));
          g.classList.toggle('is-open', open);
          barOpen = open ? titleOf(g) : '';
        });
      }
      // the chosen sort order by name (filters show their count themselves, like "Brand (2)")
      const radio = g.querySelector('input[type="radio"]:checked');
      const ticks = g.querySelectorAll('input[type="checkbox"]:checked').length;
      const labelOf = (i) => {
        const l = form.querySelector('label[for="' + i.id + '"]');
        return ((l && l.textContent) || '').replace(/\(\d+\)/, '').replace(/\s+/g, ' ').trim();
      };
      const text = radio && radio.value !== 'manual' ? labelOf(radio) : '';
      let val = btn.querySelector('.ikn-fbar__val');
      if (!val) {
        val = document.createElement('span');
        val.className = 'ikn-fbar__val';
        (btn.lastElementChild ? btn.lastElementChild.before(val) : btn.appendChild(val));
      }
      if (val.textContent !== text) val.textContent = text;
      g.classList.toggle('has-value', !!ticks);
    });
    if (barOpen && !form.querySelector('.ikn-fbar__group.is-open')) {
      const again = groups.find((g) => titleOf(g) === barOpen);
      if (again) again.classList.add('is-open');
    }

    // chips for the chosen filters
    const checked = [...form.querySelectorAll('input[type="checkbox"][name^="filter."]:checked')];
    const sig = checked.map((i) => i.id).join('|');
    let chips = form.querySelector('.ikn-fbar__chips');
    if (!chips) {
      chips = document.createElement('div');
      chips.className = 'ikn-fbar__chips';
      form.appendChild(chips);
    }
    if (chips.__sig !== sig) {
      chips.__sig = sig;
      chips.textContent = '';
      checked.forEach((i) => {
        const l = form.querySelector('label[for="' + i.id + '"]');
        const name = ((l && l.textContent) || i.value).replace(/\(\d+\)/, '').replace(/\s+/g, ' ').trim();
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'ikn-fbar__chip';
        chip.setAttribute('aria-label', T('shop.remove_filter', { name }));
        chip.textContent = name + ' ';
        const x = document.createElement('span');
        x.setAttribute('aria-hidden', 'true');
        x.textContent = '×';
        chip.appendChild(x);
        // the theme redraws the form, so look the checkbox up again at the moment of the click
        chip.addEventListener('click', () => {
          const box = document.getElementById(i.id);
          if (box) box.click();
        });
        chips.appendChild(chip);
      });
    }
  };

  /* Long filter lists (brands, designers) get a search field at the top; what was typed stays when
     the theme redraws the form after a tick. Phones: "Filter" and "Sort" become pills and the sort
     list a sheet from the bottom with its title; a tap on the dimmed page or a choice closes it.
     Styles in ik-shop.css ("Filter and sort lists in the Next style"). */
  const finds = {};
  const norm = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const findIn = (ul, q) => {
    const n = norm(q.trim());
    let shown = 0;
    [...ul.children].forEach((li) => {
      const hit = !n || norm(li.textContent).includes(n);
      if (li.classList.contains('ikn-ffind-out') === hit) li.classList.toggle('ikn-ffind-out', !hit);
      if (hit) shown += 1;
    });
    const none = ul.nextElementSibling;
    if (none && none.classList.contains('ikn-ffind__none') && none.hidden !== shown > 0) none.hidden = shown > 0;
  };
  const filterNext = () => {
    ['filters-form-sidebar', 'filters-form-drawer'].forEach((id) => {
      const form = document.getElementById(id);
      if (!form) return;
      form.querySelectorAll('ul.space-y-1').forEach((ul) => {
        if (ul.children.length <= 10 || !ul.querySelector('input[type="checkbox"]')) return;
        const g = ul.closest('data-island[x-data]');
        const head = g && g.querySelector('.ikn-fhead button');
        const name = ((head && head.firstChild && head.firstChild.textContent) || '').trim();
        const key = id + ':' + name;
        let box = ul.previousElementSibling;
        if (!box || !box.classList.contains('ikn-ffind')) {
          box = document.createElement('div');
          box.className = 'ikn-ffind';
          const input = document.createElement('input');
          input.type = 'search';
          input.autocomplete = 'off';
          input.enterKeyHint = 'search';
          input.placeholder = T('shop.filter_find');
          input.setAttribute('aria-label', T('shop.filter_find_in', { name }));
          input.value = finds[key] || '';
          // Enter would send the filter form
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') e.preventDefault();
          });
          input.addEventListener('input', () => {
            finds[key] = input.value;
            findIn(ul, input.value);
          });
          box.appendChild(input);
          ul.before(box);
          const none = document.createElement('p');
          none.className = 'ikn-ffind__none';
          none.textContent = T('shop.filter_none');
          none.hidden = true;
          ul.after(none);
        }
        findIn(ul, finds[key] || '');
      });
    });

    // sort names as Shopify sends them ("meistverkauft"): only the first letter large, not Every Word
    document.querySelectorAll('.filter-checkbox[type="radio"] + label').forEach((l) => {
      const n = [...l.childNodes].find((c) => c.nodeType === 3 && c.textContent.trim());
      if (!n) return;
      const t = n.textContent.replace(/^(\s*)(\S)/, (m, sp, c) => sp + c.toLocaleUpperCase());
      if (t !== n.textContent) n.textContent = t;
    });

    const sortBtn = document.getElementById('sort-drop-down-toggle');
    const filterBtn = [...document.querySelectorAll('button')].find((b) => /modals\.open\('filters'\)/.test(b.getAttribute('@click') || ''));
    [sortBtn, filterBtn].forEach((b) => b && !b.classList.contains('ikn-pbar__btn') && b.classList.add('ikn-pbar__btn'));
    const bar = filterBtn && filterBtn.closest('div.md\\:hidden');
    if (bar && !bar.classList.contains('ikn-pbar')) bar.classList.add('ikn-pbar');
    const sheet = document.querySelector('#sort-drop-down .sort-wrapper');
    if (sheet && sortBtn && !sheet.querySelector('.ikn-sheet__title')) {
      const t = document.createElement('p');
      t.className = 'ikn-sheet__title';
      t.setAttribute('aria-hidden', 'true');
      t.textContent = ((sortBtn.firstChild && sortBtn.firstChild.textContent) || '').trim();
      sheet.prepend(t);
    }
  };
  const closeSort = () => {
    const btn = document.getElementById('sort-drop-down-toggle');
    if (btn && btn.getAttribute('aria-expanded') === 'true') btn.click();
  };
  document.addEventListener('click', (e) => {
    if (e.target.id === 'sort-drop-down') closeSort();
  });
  document.addEventListener('change', (e) => {
    if (e.target.closest && e.target.closest('#sort-drop-down') && window.matchMedia('(max-width: 767px)').matches) window.setTimeout(closeSort, 180);
  });

  if (document.getElementById('filters-form-sidebar')) {
    filterBar();
    filterNext();
    let barTimer = 0;
    new MutationObserver(() => {
      window.clearTimeout(barTimer);
      barTimer = window.setTimeout(() => {
        filterBar();
        filterNext();
      }, 120);
    }).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('click', (e) => {
      if (e.target.closest('.ikn-fbar__group') || !e.target.isConnected) return;
      barOpen = '';
      document.querySelectorAll('.ikn-fbar__group.is-open').forEach((g) => g.classList.remove('is-open'));
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      barOpen = '';
      document.querySelectorAll('.ikn-fbar__group.is-open').forEach((g) => g.classList.remove('is-open'));
    });
  }
})();
